/**
 * CR-5362 Phase 3: surfaces the customer's per-question review decision
 * to the supplier on FillQuestionnaire when the questionnaire has been
 * sent back (status = more_info_required).
 *
 * - Approved / Approved-with-concerns answers: read-only, green banner
 *   above the question. The supplier cannot edit them — the customer
 *   has already signed them off and the rollup gate carries the
 *   decision through to the final approve.
 * - Rejected answers: editable with a red border + a banner showing
 *   the reviewer's comment so the supplier knows what to change.
 * - Pending / no decision: passes through to QuestionRenderer with
 *   no extra chrome, same as a fresh fill.
 *
 * Per the CR description "Previously approved questions should remain
 * unchanged" — locking is the chosen path. The submit endpoint resets
 * rejected answers to pending_review on resubmission so they re-enter
 * the reviewer queue; approved decisions persist.
 */
import type { ReactNode } from 'react';
import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import type { FilledDatasetValue, QuestionReviewStatus } from '../../lib/api';

interface QuestionResubmissionWrapperProps {
  reviewedValue: FilledDatasetValue | undefined;
  /** Set true when the parent questionnaire is more_info_required, i.e.
   *  the supplier is resubmitting and we want to communicate prior
   *  reviewer decisions. */
  resubmissionMode: boolean;
  children: ReactNode;
}

export function getQuestionLockState(
  reviewedValue: FilledDatasetValue | undefined,
  resubmissionMode: boolean,
): { locked: boolean; status: QuestionReviewStatus | null } {
  if (!resubmissionMode || !reviewedValue) return { locked: false, status: null };
  const status = (reviewedValue.question_status ?? null) as QuestionReviewStatus | null;
  if (status === 'approved' || status === 'approved_with_concerns') {
    return { locked: true, status };
  }
  return { locked: false, status };
}

export default function QuestionResubmissionWrapper({
  reviewedValue,
  resubmissionMode,
  children,
}: QuestionResubmissionWrapperProps) {
  if (!resubmissionMode || !reviewedValue?.question_status) {
    return <>{children}</>;
  }

  const status = reviewedValue.question_status;
  const reviewerName = reviewedValue.reviewed_by_name;
  const reviewerComment = reviewedValue.reviewer_comment;
  const reviewedAt = reviewedValue.reviewed_at
    ? new Date(reviewedValue.reviewed_at).toLocaleString()
    : '';

  if (status === 'approved' || status === 'approved_with_concerns') {
    const isWithConcerns = status === 'approved_with_concerns';
    return (
      <div className={`rounded-lg border-l-4 ${isWithConcerns ? 'border-l-amber-400' : 'border-l-green-500'}`}>
        <div className={`px-3 py-2 text-xs flex flex-wrap items-center gap-2 ${
          isWithConcerns ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'
        }`}>
          {isWithConcerns ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
          <span className="font-medium">
            {isWithConcerns ? 'Approved with concerns' : 'Approved'} by reviewer · locked
          </span>
          {reviewerName && (
            <span className="opacity-80">
              · {reviewerName}
              {reviewedAt && ` · ${reviewedAt}`}
            </span>
          )}
          {reviewerComment && (
            <span className="basis-full italic mt-1">{reviewerComment}</span>
          )}
        </div>
        {children}
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="rounded-lg border-l-4 border-l-red-500">
        <div className="px-3 py-2 text-xs flex flex-wrap items-center gap-2 bg-red-50 text-red-800">
          <XCircle className="h-3.5 w-3.5" />
          <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300 text-[10px]">
            Needs revision
          </Badge>
          {reviewerName && (
            <span className="opacity-80">
              · {reviewerName}
              {reviewedAt && ` · ${reviewedAt}`}
            </span>
          )}
          {reviewerComment && (
            <span className="basis-full italic mt-1">Reviewer note: {reviewerComment}</span>
          )}
        </div>
        {children}
      </div>
    );
  }

  return <>{children}</>;
}
